import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import SEO from './SEO';
import './Signup.css';
import { needsProfileCompletion } from "../utils/profileCompletion";

const Signup2 = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [firmName, setFirmName] = useState('');
    const [role, setRole] = useState('Attorney');
    const [showPassword, setShowPassword] = useState(false);
    const { showWarning } = useNotification();
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [googleTermsHint, setGoogleTermsHint] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({ email: '', password: '', firmName: '' });
    const [touched, setTouched] = useState({ email: false, password: false, firmName: false });
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [googleInitialized, setGoogleInitialized] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const fromState = location.state?.from;
    const redirectPath = typeof fromState === 'string' ? fromState : fromState?.pathname || '/research';
    const googleButtonRef = React.useRef(null);
    const userInitiatedGoogleAction = React.useRef(false);

    // Check for tester/user invitation token in URL
    const [testerToken, setTesterToken] = useState(null);
    const [userToken, setUserToken] = useState(null);
    const [invitationEmail, setInvitationEmail] = useState('');

    const { invitedSignup, signInWithGoogle, isAuthenticated, loading: authLoading, user } = useAuth();

    useEffect(() => {
        if (!authLoading && isAuthenticated) {
            setError(''); // Clear any errors when authenticated
            if (needsProfileCompletion(user)) {
                navigate("/complete-profile", { replace: true, state: { from: redirectPath } });
            } else {
                navigate(redirectPath, { replace: true });
            }
        }
    }, [isAuthenticated, authLoading, navigate, redirectPath, user]);

    useEffect(() => {
        setError('');
        setFieldErrors({ email: '', password: '', firmName: '' });
        setTouched({ email: false, password: false, firmName: false });

        const urlParams = new URLSearchParams(location.search);
        const testerTokenParam = urlParams.get('testerToken');
        const userTokenParam = urlParams.get('userToken');
        const emailParam = urlParams.get('email');

        if (testerTokenParam && emailParam) {
            setTesterToken(testerTokenParam);
            setInvitationEmail(emailParam);
            setEmail(emailParam);
        } else if (userTokenParam && emailParam) {
            setUserToken(userTokenParam);
            setInvitationEmail(emailParam);
            setEmail(emailParam);
        }
    }, [location.search]);

    const handleGoogleCallback = React.useCallback(async (response) => {
        if (!response.credential) {
            setError('Google sign-in failed. Please try again.');
            setGoogleLoading(false);
            userInitiatedGoogleAction.current = false;
            return;
        }

        setGoogleLoading(true);
        setError(''); // Clear any previous errors

        const result = await signInWithGoogle(response.credential);
        setGoogleLoading(false);
        userInitiatedGoogleAction.current = false; // Reset flag after callback

        if (result.success) {
            setError(''); // Clear errors on success
            if (needsProfileCompletion(user)) {
                navigate("/complete-profile", { replace: true, state: { from: redirectPath } });
            } else {
                navigate(redirectPath, { replace: true });
            }
        } else {
            // Show all error messages to the user
            if (result.message) {
                setError(result.message);
            } else {
                setError('Google sign-in failed. Please try again.');
            }
        }
    }, [signInWithGoogle, navigate, redirectPath, user]);

    // Handle Google sign-in errors - only for actual user-facing errors
    useEffect(() => {
        const handleGoogleError = (error) => {
            setGoogleLoading(false);

            // Don't show errors for user cancellations
            if (error.type === 'popup_closed_by_user' || error.type === 'user_cancelled') {
                userInitiatedGoogleAction.current = false;
                return;
            }

            // Only show errors if user actually tried to use Google sign-in
            if (!userInitiatedGoogleAction.current) {
                return;
            }

            const errorMessage = error.message || error.detail?.message || error.error || '';

            // Only show critical errors that prevent functionality
            if (errorMessage.includes('popup_blocked')) {
                setError('Google Sign-In Error: Popup was blocked. Please allow popups for this site and try again.');
            } else if (errorMessage.includes('access_denied')) {
                // User denied access, don't show error
                userInitiatedGoogleAction.current = false;
                return;
            } else if (errorMessage && !errorMessage.includes('invalid_client') && !errorMessage.includes('401') && !errorMessage.includes('no registered origin')) {
                // Only show non-configuration errors for user actions
                setError(`Google Sign-In Error: ${errorMessage}`);
            }

            // Reset flag after handling
            userInitiatedGoogleAction.current = false;
        };

        // Listen for Google errors from the library - only handle actual error events
        const errorHandler = (e) => {
            // Only handle if it's a real error event, not console warnings
            if (e.type === 'error' || e.detail) {
                handleGoogleError(e.detail || e);
            }
        };

        window.addEventListener('google-signin-error', errorHandler);

        return () => {
            window.removeEventListener('google-signin-error', errorHandler);
        };
    }, []);

    // Load Google Identity Services script
    useEffect(() => {
        // Cancel any existing One Tap prompts immediately on mount
        const cancelOneTap = () => {
            if (window.google && window.google.accounts && window.google.accounts.id) {
                try {
                    window.google.accounts.id.cancel();
                } catch (e) {
                    // Ignore errors
                }
            }
        };

        // Cancel immediately
        cancelOneTap();

        if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
            console.warn('Google Client ID not configured. Google sign-in will not work.');
            return;
        }

        // Set up interval to keep canceling One Tap (in case it tries to show)
        const cancelInterval = setInterval(() => {
            cancelOneTap();
        }, 500);

        // Check if script is already loaded
        const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
        if (existingScript) {
            // Script already loaded, just initialize (but don't auto-prompt)
            if (window.google && window.google.accounts) {
                try {
                    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
                    if (!clientId) {
                        // Don't show error on initialization - only when user tries to use it
                        return;
                    }

                    cancelOneTap(); // Cancel any existing prompts
                    window.google.accounts.id.initialize({
                        client_id: clientId,
                        callback: handleGoogleCallback,
                        auto_select: false, // Don't auto-select
                        cancel_on_tap_outside: true, // Cancel if user clicks outside
                        itp_support: false, // Disable One Tap completely
                    });
                    // Explicitly cancel any prompts that might have been triggered
                    setTimeout(() => {
                        try {
                            window.google.accounts.id.cancel();
                        } catch (e) { }
                    }, 100);

                    // Render the hidden Google button
                    if (window.google.accounts.id && googleButtonRef.current) {
                        try {
                            window.google.accounts.id.renderButton(googleButtonRef.current, {
                                theme: 'outline',
                                size: 'large',
                                text: 'signup_with',
                                width: 300,
                            });
                            setGoogleInitialized(true);
                            setError(''); // Clear any errors if initialization succeeds
                        } catch (error) {
                            console.error('Error rendering Google button:', error);
                            // Don't show error if it's just a configuration warning and button might still work
                            // Only log to console, don't show to user unless they try to use it
                        }
                    }
                } catch (error) {
                    console.error('Error initializing Google Sign-In:', error);
                    // Don't show initialization errors to user - they might be false positives
                    // Only show errors when user actually tries to use Google sign-in
                }
            }
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            if (window.google && window.google.accounts) {
                try {
                    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
                    if (!clientId) {
                        // Don't show error on initialization - only when user tries to use it
                        return;
                    }

                    cancelOneTap(); // Cancel any existing prompts
                    window.google.accounts.id.initialize({
                        client_id: clientId,
                        callback: handleGoogleCallback,
                        auto_select: false, // Don't auto-select
                        cancel_on_tap_outside: true, // Cancel if user clicks outside
                        itp_support: false, // Disable One Tap completely
                    });
                    // Explicitly cancel any prompts that might have been triggered
                    setTimeout(() => {
                        try {
                            window.google.accounts.id.cancel();
                        } catch (e) { }
                    }, 100);

                    // Render the hidden Google button
                    if (window.google.accounts.id && googleButtonRef.current) {
                        try {
                            window.google.accounts.id.renderButton(googleButtonRef.current, {
                                theme: 'outline',
                                size: 'large',
                                text: 'signup_with',
                                width: 300,
                            });
                            setGoogleInitialized(true);
                            setError(''); // Clear any errors if initialization succeeds
                        } catch (error) {
                            console.error('Error rendering Google button:', error);
                            // Don't show error if it's just a configuration warning and button might still work
                            // Only log to console, don't show to user unless they try to use it
                        }
                    }
                } catch (error) {
                    console.error('Error initializing Google Sign-In:', error);
                    // Don't show initialization errors to user - they might be false positives
                    // Only show errors when user actually tries to use Google sign-in
                }
            }
        };
        script.onerror = () => {
            console.error('Failed to load Google Identity Services script');
        };
        document.body.appendChild(script);

        // Cancel One Tap on component mount/unmount
        cancelOneTap();

        return () => {
            // Cancel One Tap when component unmounts and clear interval
            clearInterval(cancelInterval);
            cancelOneTap();
        };
    }, [handleGoogleCallback]);

    const validateEmail = (emailValue) => {
        if (!emailValue) return 'Email address is required';
        const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(emailValue)) return 'Please enter a valid email address.';
        return '';
    };

    const validatePassword = (passwordValue) => {
        if (!passwordValue) return 'Password is required';
        if (passwordValue.length < 8) return 'Password must be at least 8 characters long';
        const hasUpperCase = /[A-Z]/.test(passwordValue);
        const hasLowerCase = /[a-z]/.test(passwordValue);
        const hasNumber = /[0-9]/.test(passwordValue);
        const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(passwordValue);
        if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar)
            return 'Password must contain uppercase, lowercase, number, and special character';
        return '';
    };

    const validateFirmName = (nameValue) => {
        if (!nameValue) return 'Firm, company, or your name is required';
        if (nameValue.trim().length === 0) return 'Firm name cannot be empty';
        return '';
    };

    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
        if (field === 'email') setFieldErrors(prev => ({ ...prev, email: validateEmail(email) }));
        else if (field === 'password') setFieldErrors(prev => ({ ...prev, password: validatePassword(password) }));
        else if (field === 'firmName') setFieldErrors(prev => ({ ...prev, firmName: validateFirmName(firmName) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        setTouched({ email: true, password: true, firmName: true });

        const emailError = validateEmail(email);
        const passwordError = validatePassword(password);
        const firmNameError = validateFirmName(firmName);

        setFieldErrors({
            email: emailError,
            password: passwordError,
            firmName: firmNameError
        });

        if (emailError || passwordError || firmNameError) return;

        if (!agreeToTerms) {
            showWarning('Please agree to the Terms & Privacy Policy before continuing.', 'Terms Required');
            return;
        }

        if (!testerToken && !userToken) {
            setError('No valid invitation found. Please use the regular signup page.');
            return;
        }

        setLoading(true);

        const result = await invitedSignup(firmName, email, password, testerToken, userToken, role);
        setLoading(false);

        if (result.success) {
            navigate(needsProfileCompletion(user) ? "/complete-profile" : redirectPath, { replace: true });
        } else {
            setError(result.message || 'Signup failed. Please try again.');
        }
    };

    const handleGoogleSignup = () => {
        setError('');
        setGoogleLoading(true);
        userInitiatedGoogleAction.current = true;

        if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
            setError('Google Sign-In is not configured.');
            setGoogleLoading(false);
            return;
        }

        if (window.google && window.google.accounts && window.google.accounts.id) {
            const googleButton = document.getElementById('google-signin-button-hidden');
            if (googleButton) {
                const buttonElement = googleButton.querySelector('div[role="button"]');
                if (buttonElement) buttonElement.click();
                else setError('Google button not ready.');
            }
        }
        setGoogleLoading(false);
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.08, delayChildren: 0.2 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeOut" },
        },
    };

    return (
        <motion.div className="signup-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <SEO title="Complete Signup" path="/signup2" description="Complete your LawFusion account setup." noIndex={true} />
            <motion.div className="signup-container" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
                <motion.div className="signup-card">
                    <div className="logo-container">
                        <Link to="/">
                            <img src="/logo.png" alt="LawFusion.AI Logo" className="login-logo" />
                        </Link>
                        <h1 className="signup-title">Complete your LawFusion Registration</h1>
                    </div>

                    <div id="google-signin-button-hidden" ref={googleButtonRef} style={{ display: 'none' }} />

                    <button type="button" className="social-button" onClick={() => { if (!agreeToTerms) { setGoogleTermsHint(true); showWarning('Please agree to the Terms & Privacy Policy before continuing.', 'Terms Required'); return; } handleGoogleSignup(); }} disabled={googleLoading}>
                        Continue With Google
                    </button>
                    {googleTermsHint && !agreeToTerms && (
                        <p style={{ color: '#e53e3e', fontSize: '0.8rem', margin: '0.4rem 0 0', textAlign: 'center' }}>
                            Please agree to the Terms & Privacy Policy below.
                        </p>
                    )}

                    <div className="separator">
                        <span className="separator-line" />
                        <span className="separator-text">or complete with email</span>
                        <span className="separator-line" />
                    </div>

                    <form onSubmit={handleSubmit} className="signup-form">
                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={() => handleBlur('email')}
                                required
                                disabled={true} // Keep invitation email fixed
                                className="input-disabled"
                                style={{ cursor: 'not-allowed', backgroundColor: '#f5f5f5', color: '#666' }}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onBlur={() => handleBlur('password')}
                                    required
                                    placeholder="Enter your password"
                                />
                                <button type="button" className="toggle-password-btn" onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            {touched.password && fieldErrors.password && <div className="field-error">{fieldErrors.password}</div>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="firmName">Firm, Company, or Your Name</label>
                            <input
                                type="text"
                                id="firmName"
                                value={firmName}
                                onChange={(e) => setFirmName(e.target.value)}
                                onBlur={() => handleBlur('firmName')}
                                required
                                placeholder="Firm, company or full name"
                            />
                            {touched.firmName && fieldErrors.firmName && <div className="field-error">{fieldErrors.firmName}</div>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="role">Role</label>
                            <select id="role" value={role} onChange={(e) => setRole(e.target.value)} className="role-select">
                                <option value="Attorney">Attorney</option>
                                <option value="Paralegal">Paralegal</option>
                                <option value="Legal Researcher">Legal Researcher</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>

                        <div className="terms-checkbox">
                            <input
                                type="checkbox"
                                id="terms"
                                checked={agreeToTerms}
                                onChange={(e) => { setAgreeToTerms(e.target.checked); if (e.target.checked) setGoogleTermsHint(false); }}
                                required
                            />
                            <label htmlFor="terms">I agree to the Terms and Privacy Policy.</label>
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <button type="submit" className="create-account-button" disabled={loading}>
                            {loading ? 'Creating Account...' : 'Complete Signup'}
                        </button>
                    </form>

                    <div className="signin-link">
                        Already have an account? <Link to="/login">Sign in</Link>
                    </div>
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

export default Signup2;
